import { useEffect, useState } from "react";
import { Loader2, Layers, Wand2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import { catalogueLabel, CATALOGUE_NAMES } from "../../lib/localize";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";

const INPUT = "w-full bg-ink border border-ivory/15 rounded-lg px-4 py-2.5 text-sm focus:border-gold focus:outline-none";
const LABEL = "block text-[10px] uppercase tracking-widest text-ivory/65 mb-1";

// Modification en lot de la Boutique : appliquer des changements (texte, photo, prix,
// frais, groupe, visibilité) à tous les items, un catalogue, une catégorie ou un groupe
// de modèles. Les produits « modifiés man. » sont protégés sauf si forcé.
export default function BulkProductTools({ onDone }) {
  const [open, setOpen] = useState(false);
  const [facets, setFacets] = useState({ categories: [], groups: [] });
  const [scope, setScope] = useState("all");
  const [catalogueNo, setCatalogueNo] = useState("");
  const [category, setCategory] = useState("");
  const [group, setGroup] = useState("");
  const [includeManual, setIncludeManual] = useState(false);
  const [fields, setFields] = useState({
    description: "", description_en: "", image: "", category: "", group: "",
    multiplier: "", fee_gravure: "", fee_impression: "", screen_charge: "", repeat_charge: "",
    active: "", shop_category: "", shop_subcategory: "",
  });
  const [busy, setBusy] = useState(false);
  const [grouping, setGrouping] = useState(false);
  const [reclassifying, setReclassifying] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get("/admin/products/facets").then((r) => setFacets(r.data)).catch(() => {});
  }, [open]);

  const set = (k, v) => setFields((f) => ({ ...f, [k]: v }));

  const scopeLabel = () => {
    if (scope === "all") return "tous les produits de la boutique";
    if (scope === "catalogue") return `le catalogue ${catalogueLabel(catalogueNo)}`;
    if (scope === "category") return `la catégorie « ${category} »`;
    return `le groupe « ${group} »`;
  };

  const apply = async () => {
    const payload = { scope, include_manual: includeManual };
    if (scope === "catalogue") payload.catalogue_no = catalogueNo;
    if (scope === "category") payload.category = category;
    if (scope === "group") payload.group = group;
    if (fields.description.trim()) payload.set_description = fields.description.trim();
    if (fields.description_en.trim()) payload.set_description_en = fields.description_en.trim();
    if (fields.image.trim()) payload.set_image = fields.image.trim();
    if (fields.category.trim()) payload.set_category = fields.category.trim();
    if (fields.group.trim()) payload.set_group = fields.group.trim();
    if (fields.multiplier !== "") payload.price_multiplier = parseFloat(fields.multiplier);
    if (fields.fee_gravure !== "") payload.set_fee_gravure = parseFloat(fields.fee_gravure);
    if (fields.fee_impression !== "") payload.set_fee_impression = parseFloat(fields.fee_impression);
    if (fields.screen_charge !== "") payload.set_screen_charge = parseFloat(fields.screen_charge);
    if (fields.repeat_charge !== "") payload.set_repeat_charge = parseFloat(fields.repeat_charge);
    if (fields.active !== "") payload.set_active = fields.active === "1";
    if (fields.shop_category) payload.set_shop_category = fields.shop_category;
    if (fields.shop_subcategory.trim()) payload.set_shop_subcategory = fields.shop_subcategory.trim();
    const changes = Object.keys(payload).filter((k) => k.startsWith("set_") || k === "price_multiplier");
    if (!changes.length) { toast.error("Remplissez au moins un changement à appliquer"); return; }
    if (!window.confirm(`Appliquer ${changes.length} changement(s) à ${scopeLabel()} ?${includeManual ? "" : " Les produits « modifiés man. » seront ignorés."}`)) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/products/bulk-update", payload);
      toast.success(`${data.modified} produit(s) modifié(s) sur ${data.matched} visé(s)`);
      onDone?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  // Regroupe automatiquement les modèles pareils (même nom sans la grandeur)
  const autoGroup = async () => {
    setGrouping(true);
    try {
      const { data: preview } = await api.post("/admin/products/auto-group", null, { params: { dry_run: true } });
      if (!preview.groups) { toast.info("Aucun modèle pareil détecté (noms identiques en plusieurs grandeurs)"); return; }
      const examples = preview.sample.map((s) => `• ${s.group} (${s.count} variantes)`).join("\n");
      if (!window.confirm(`${preview.groups} groupes détectés (${preview.products} produits).\n\nExemples :\n${examples}\n\nRegrouper ces modèles ? Les regroupements faits à la main ne sont jamais écrasés.`)) return;
      const { data } = await api.post("/admin/products/auto-group", null, { params: { dry_run: false } });
      toast.success(`${data.updated} produit(s) regroupé(s) en ${data.groups} groupes`);
      api.get("/admin/products/facets").then((r) => setFacets(r.data)).catch(() => {});
      onDone?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setGrouping(false);
    }
  };

  // Reclasse automatiquement tous les produits dans les catégories boutique
  const reclassify = async () => {
    if (!window.confirm("Reclasser automatiquement TOUS les produits (Trophées / Médailles / Plaques / Autres + sous-catégories) ? Les classements faits à la main sur les produits « modifiés man. » sont conservés.")) return;
    setReclassifying(true);
    try {
      const { data } = await api.post("/admin/products/reclassify");
      toast.success(`${data.updated} produit(s) reclassé(s) (${data.skipped_manual} manuels conservés)`);
      api.get("/admin/products/facets").then((r) => setFacets(r.data)).catch(() => {});
      onDone?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setReclassifying(false);
    }
  };

  if (!open) {
    return (
      <button data-testid="bulk-tools-open" onClick={() => setOpen(true)}
        className="mb-8 ml-3 inline-flex items-center gap-2 border border-ivory/25 text-ivory/70 px-6 py-3 rounded-full hover:border-gold hover:text-gold transition-colors duration-300">
        <Layers className="w-4 h-4" /> Modification en lot / regroupement
      </button>
    );
  }

  return (
    <div className="mb-8 bg-surface border border-gold/25 rounded-xl p-6" data-testid="bulk-tools">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h3 className="font-serif text-xl text-gold">Modification en lot de la Boutique</h3>
          <p className="text-xs text-ivory/70 mt-1">Appliquez un changement à tous les items, à un catalogue, à une catégorie ou à un groupe de modèles. Pour un seul item, utilisez le crayon de sa ligne.</p>
        </div>
        <button data-testid="bulk-tools-close" onClick={() => setOpen(false)}
          className="text-xs border border-ivory/20 text-ivory/60 px-4 py-2 rounded-full hover:border-gold/50 hover:text-gold transition-colors duration-300">
          Refermer
        </button>
      </div>

      {/* Portée */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
        <label>
          <span className={LABEL}>Appliquer à</span>
          <Select value={scope} onValueChange={setScope}>
            <SelectTrigger data-testid="bulk-scope" className="bg-ink border-ivory/15"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-surface border-gold/20 text-ivory">
              <SelectItem value="all" data-testid="bulk-scope-all">Tous les items</SelectItem>
              <SelectItem value="catalogue" data-testid="bulk-scope-catalogue">Un catalogue</SelectItem>
              <SelectItem value="category" data-testid="bulk-scope-category">Une catégorie</SelectItem>
              <SelectItem value="group" data-testid="bulk-scope-group" disabled={!facets.groups.length}>
                Un groupe de modèles{facets.groups.length ? "" : " (aucun pour l'instant)"}
              </SelectItem>
            </SelectContent>
          </Select>
        </label>
        {scope === "catalogue" && (
          <label>
            <span className={LABEL}>Catalogue</span>
            <Select value={catalogueNo} onValueChange={setCatalogueNo}>
              <SelectTrigger data-testid="bulk-catalogue" className="bg-ink border-ivory/15"><SelectValue placeholder="Choisir…" /></SelectTrigger>
              <SelectContent className="bg-surface border-gold/20 text-ivory max-h-72">
                {Object.keys(CATALOGUE_NAMES).sort((a, b) => a.localeCompare(b, undefined, { numeric: true })).map((no) => (
                  <SelectItem key={no} value={no} data-testid={`bulk-catalogue-${no}`}>{catalogueLabel(no)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </label>
        )}
        {scope === "category" && (
          <label>
            <span className={LABEL}>Catégorie</span>
            <input list="bulk-categories" data-testid="bulk-category" value={category} onChange={(e) => setCategory(e.target.value)}
              placeholder="Ex. : Trophées" className={INPUT} />
            <datalist id="bulk-categories">
              {facets.categories.map((c) => <option key={c} value={c} />)}
            </datalist>
          </label>
        )}
        {scope === "group" && (
          <label>
            <span className={LABEL}>Groupe</span>
            <input list="bulk-groups" data-testid="bulk-group" value={group} onChange={(e) => setGroup(e.target.value)}
              placeholder="Nom du groupe" className={INPUT} />
            <datalist id="bulk-groups">
              {facets.groups.map((g) => <option key={g} value={g} />)}
            </datalist>
          </label>
        )}
        <label className="flex items-end gap-2.5 pb-1 select-none cursor-pointer">
          <input type="checkbox" data-testid="bulk-include-manual" checked={includeManual}
            onChange={(e) => setIncludeManual(e.target.checked)} className="w-4 h-4 accent-[#C9A24D]" />
          <span className="text-xs text-ivory/60">Toucher aussi les produits « modifiés man. »</span>
        </label>
      </div>

      {/* Changements */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-5">
        <label>
          <span className={LABEL}>Prix × multiplicateur</span>
          <input data-testid="bulk-multiplier" type="number" step="0.01" min="0.01" max="10" value={fields.multiplier}
            onChange={(e) => set("multiplier", e.target.value)} placeholder="Ex. : 2.5" className={INPUT} />
        </label>
        <label>
          <span className={LABEL}>Catégorie (nouveau nom)</span>
          <input data-testid="bulk-set-category" value={fields.category} onChange={(e) => set("category", e.target.value)}
            placeholder="Laisser vide = inchangé" className={INPUT} />
        </label>
        <label>
          <span className={LABEL}>Photo (URL ou /api/files/…)</span>
          <input data-testid="bulk-set-image" value={fields.image} onChange={(e) => set("image", e.target.value)}
            placeholder="Laisser vide = inchangé" className={INPUT} />
        </label>
        <label>
          <span className={LABEL}>Frais gravure ($ / unité)</span>
          <input data-testid="bulk-fee-gravure" type="number" step="0.01" min="0" value={fields.fee_gravure}
            onChange={(e) => set("fee_gravure", e.target.value)} placeholder="Inchangé" className={INPUT} />
        </label>
        <label>
          <span className={LABEL}>Frais impression ($ / unité)</span>
          <input data-testid="bulk-fee-impression" type="number" step="0.01" min="0" value={fields.fee_impression}
            onChange={(e) => set("fee_impression", e.target.value)} placeholder="Inchangé" className={INPUT} />
        </label>
        <label>
          <span className={LABEL}>Frais d'écran ($)</span>
          <input data-testid="bulk-screen-charge" type="number" step="0.01" min="0" value={fields.screen_charge}
            onChange={(e) => set("screen_charge", e.target.value)} placeholder="Inchangé" className={INPUT} />
        </label>
        <label>
          <span className={LABEL}>Frais de répétition ($)</span>
          <input data-testid="bulk-repeat-charge" type="number" step="0.01" min="0" value={fields.repeat_charge}
            onChange={(e) => set("repeat_charge", e.target.value)} placeholder="Inchangé" className={INPUT} />
        </label>
        <label>
          <span className={LABEL}>Groupe (nouveau)</span>
          <input data-testid="bulk-set-group" value={fields.group} onChange={(e) => set("group", e.target.value)}
            placeholder="Laisser vide = inchangé" className={INPUT} />
        </label>
        <label>
          <span className={LABEL}>Visibilité</span>
          <Select value={fields.active} onValueChange={(v) => set("active", v)}>
            <SelectTrigger data-testid="bulk-active" className="bg-ink border-ivory/15"><SelectValue placeholder="Inchangé" /></SelectTrigger>
            <SelectContent className="bg-surface border-gold/20 text-ivory">
              <SelectItem value="1" data-testid="bulk-active-1">Actif (visible)</SelectItem>
              <SelectItem value="0" data-testid="bulk-active-0">Inactif (masqué)</SelectItem>
            </SelectContent>
          </Select>
        </label>
        <label>
          <span className={LABEL}>Catégorie boutique (reclasser)</span>
          <Select value={fields.shop_category} onValueChange={(v) => set("shop_category", v)}>
            <SelectTrigger data-testid="bulk-shop-category" className="bg-ink border-ivory/15"><SelectValue placeholder="Inchangé" /></SelectTrigger>
            <SelectContent className="bg-surface border-gold/20 text-ivory">
              {["Trophées", "Médailles", "Plaques", "Autres"].map((c) => (
                <SelectItem key={c} value={c} data-testid={`bulk-shopcat-${c.toLowerCase()}`}>{c}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </label>
        <label>
          <span className={LABEL}>Sous-catégorie boutique</span>
          <input list="bulk-shop-subcats" data-testid="bulk-shop-subcategory" value={fields.shop_subcategory}
            onChange={(e) => set("shop_subcategory", e.target.value)}
            placeholder="Ex. : Coupe Métal — vide = inchangé" className={INPUT} />
          <datalist id="bulk-shop-subcats">
            {(facets.shop_subcategories || []).map((s) => <option key={s} value={s} />)}
          </datalist>
        </label>
        <label className="sm:col-span-2 lg:col-span-3">
          <span className={LABEL}>Description (français)</span>
          <textarea data-testid="bulk-set-description" rows={2} value={fields.description}
            onChange={(e) => set("description", e.target.value)} placeholder="Laisser vide = inchangé" className={INPUT} />
        </label>
        <label className="sm:col-span-2 lg:col-span-3">
          <span className={LABEL}>Description (anglais)</span>
          <textarea data-testid="bulk-set-description-en" rows={2} value={fields.description_en}
            onChange={(e) => set("description_en", e.target.value)} placeholder="Leave empty = unchanged" className={INPUT} />
        </label>
      </div>

      <div className="flex flex-wrap gap-3">
        <button data-testid="bulk-apply" onClick={apply} disabled={busy}
          className="inline-flex items-center gap-2 bg-gold text-ink font-semibold px-6 py-3 rounded-full text-sm hover:bg-champagne transition-colors duration-300 disabled:opacity-60">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Layers className="w-4 h-4" />}
          Appliquer à la sélection
        </button>
        <button data-testid="bulk-auto-group" onClick={autoGroup} disabled={grouping}
          className="inline-flex items-center gap-2 border border-gold/40 text-gold px-6 py-3 rounded-full text-sm hover:bg-gold hover:text-ink transition-colors duration-300 disabled:opacity-60">
          {grouping ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />}
          Regrouper les modèles pareils
        </button>
        <button data-testid="bulk-reclassify" onClick={reclassify} disabled={reclassifying}
          className="inline-flex items-center gap-2 border border-gold/40 text-gold px-6 py-3 rounded-full text-sm hover:bg-gold hover:text-ink transition-colors duration-300 disabled:opacity-60">
          {reclassifying ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />}
          Reclasser automatiquement
        </button>
      </div>
      <p className="text-xs text-ivory/65 mt-3">« Regrouper les modèles pareils » détecte les produits au nom identique offerts en plusieurs grandeurs et les regroupe pour les modifier ensemble. Aperçu demandé avant application.</p>
    </div>
  );
}
